import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import AiCardBase from './AiCardBase';

const isCurrentItem = (item) => item?.isCurrent === true || item?.isCurrent === 'true';

const getCurrentObservation = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) {
    return data.find(isCurrentItem) || data[0] || null;
  }
  if (Array.isArray(data.observations)) {
    return data.observations.find(isCurrentItem) || data.observations[0] || null;
  }
  if (typeof data === 'object') {
    return data;
  }
  return null;
};

const extractObservationText = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;

  const currentItem = getCurrentObservation(data);
  if (!currentItem) return '';

  if (typeof currentItem === 'string') return currentItem;

  return (
    currentItem.observations ||
    currentItem.observation ||
    currentItem.notes ||
    currentItem.description ||
    (typeof data === 'object' && typeof data.observations === 'string' ? data.observations : '') ||
    (typeof data === 'object' && (data.notes || data.observation || data.description)) ||
    ''
  );
};

const ObservationAiCard = ({ card = {}, onConfirm, isConfirmed, isConfirming }) => {
  const { cardType, label, data = {} } = card;

  const currentObs = getCurrentObservation(data);
  const initialText = extractObservationText(data);

  const [text, setText] = useState(initialText);

  useEffect(() => {
    setText(extractObservationText(data));
  }, [data]);

  const handleConfirm = () => {
    let payload;
    if (Array.isArray(data)) {
      const hasCurrent = data.some(isCurrentItem);
      payload = data.map((item, idx) =>
        (isCurrentItem(item) || (!hasCurrent && idx === data.length - 1))
          ? (typeof item === 'object' && item !== null ? { ...item, observations: text } : text)
          : item
      );
    } else if (Array.isArray(data?.observations)) {
      const hasCurrent = data.observations.some(isCurrentItem);
      const updatedObservations = data.observations.map((item, idx) =>
        (isCurrentItem(item) || (!hasCurrent && idx === data.observations.length - 1))
          ? (typeof item === 'object' && item !== null ? { ...item, observations: text } : text)
          : item
      );
      payload = {
        ...data,
        observations: updatedObservations,
        notes: text,
      };
    } else if (typeof data === 'object' && data !== null) {
      payload = { ...data, notes: text, observations: text };
    } else {
      payload = { notes: text, observations: text };
    }

    onConfirm(cardType, payload);
  };

  const followUpDate = currentObs?.followUpDate || data?.followUpDate;

  return (
    <AiCardBase
      cardType={cardType}
      label={label || "Doctor's notes / observations"}
      isConfirmed={isConfirmed}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      isUpdatedByAi={card.isUpdatedByAi ?? currentObs?.isUpdatedByAi}
    >
      <Box sx={{ py: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
            AI Extracted Clinical Observations:
          </Typography>
          {currentObs?.week !== undefined && currentObs?.week !== null && (
            <Chip
              label={`Week ${currentObs.week}`}
              size="small"
              sx={{
                fontSize: 11,
                height: 22,
                bgcolor: '#EFF6FF',
                color: '#1D4ED8',
                fontWeight: 600,
              }}
            />
          )}
        </Stack>

        <TextField
          fullWidth
          multiline
          rows={4}
          disabled={isConfirmed}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Doctor's clinical notes, observations, and assessment..."
          data-testid="input-observation-notes"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#FFFFFF',
              borderRadius: 2,
            },
          }}
        />

        {followUpDate && (
          <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Suggested follow-up:
            </Typography>
            <Chip label={followUpDate} size="small" sx={{ fontSize: 11, height: 22 }} />
          </Stack>
        )}
      </Box>
    </AiCardBase>
  );
};

export default ObservationAiCard;
